import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from 'helpers/api';
import { markAsGuarded } from 'helpers/candidate_application/candidate-submission-guard';

import { calculateIdRequirements } from '../helpers/candidate_application/id-requirements';
import { processStaticContent } from '../helpers/candidate_application/process-static-content';
import { camelCaseInnerKeys } from '../helpers/text';

const initialState = {
  fetched: false, // Data fetched

  // Original application data
  application: {
    locale: 'en_GB',
    country: 'NZ',
    candidate: {},
    brand: {},
    candidate_checks: [],
    check_types: [],
    forms: [],
    referees: [],
    id_uploads: [],
    other_uploads: [],
    check_values: {
      values: {},
      system_values: {},
    },
    candidate_fields: {},
  },

  candidateFields: {
    sections: [],
    templateSources: {},
    hiddenFields: {},
    candidateValues: {},
    hiddenValues: {},
    formValues: {}, // Store all form values from all sections
    sectionFieldPrefixes: {},
    fieldPathIndex: {},
    wrapperValidators: {},
    excludeOnSubmitCodes: [],
    saveSelectedIdListCodes: [],
    dynamicDependencyCodes: {},
    hiddenDependencyCodes: {},
    validationDependencyCodes: {},
    idRequirementsDependencyCodes: [],
    hasIdRequirements: false,
    idRequirementsMet: {},
    checkTypeIdRequirements: {},
    selectedIdDocuments: {},
    photoIdOptions: [],
    dobIdOptions: [],
  },
};

// updateCandidateSections carries structural/dynamic-content changes only (e.g. an
// id_requirements_list's accepted-options set swapping based on a dependent field like
// citizenship). It must never be trusted as the source of truth for
// requirementsMet/verifiedRequirementsMet — that's calculateIdRequirements's job exclusively,
// dispatched separately via recalculateIdRequirements/updateIdRequirements. Without this, a
// dynamic-content swap that lands without an immediately-following fresh recalculation (e.g.
// a stale retry closure racing a live field-change dispatch) can silently drag forward
// whatever requirementsMet the field had before the swap — including a stale "met" from when
// the list was previously invisible — onto its newly-swapped, actually-unmet option set.
const collectRequirementsMetByCode = sections => {
  const map = new Map();
  (sections || []).forEach(section => {
    (section?.subsections || []).forEach(subsection => {
      (subsection?.fields || []).forEach(field => {
        if (field?.code && Object.prototype.hasOwnProperty.call(field, 'requirementsMet')) {
          map.set(field.code, {
            requirementsMet: field.requirementsMet,
            verifiedRequirementsMet: field.verifiedRequirementsMet,
          });
        }
        (field?.subFields || []).forEach(subField => {
          if (subField?.code && Object.prototype.hasOwnProperty.call(subField, 'requirementsMet')) {
            map.set(subField.code, {
              requirementsMet: subField.requirementsMet,
              verifiedRequirementsMet: subField.verifiedRequirementsMet,
            });
          }
        });
      });
    });
  });
  return map;
};

const preserveRequirementsMet = (newSections, preservedByCode) => {
  if (!preservedByCode.size) return newSections;
  return (newSections || []).map(section => ({
    ...section,
    subsections: (section?.subsections || []).map(subsection => ({
      ...subsection,
      fields: (subsection?.fields || []).map(field => {
        let nextField = field;
        if (field?.code && preservedByCode.has(field.code)) {
          nextField = { ...nextField, ...preservedByCode.get(field.code) };
        }
        if (Array.isArray(field?.subFields)) {
          const nextSubFields = field.subFields.map(subField =>
            subField?.code && preservedByCode.has(subField.code)
              ? { ...subField, ...preservedByCode.get(subField.code) }
              : subField,
          );
          nextField = { ...nextField, subFields: nextSubFields };
        }
        return nextField;
      }),
    })),
  }));
};

const updateIdRequirements = state => {
  // Step 1: Flatten formValues from sections into a single object
  const flattenedFormValues = Object.values(state.candidateFields.formValues).reduce(
    (acc, sectionValues) => {
      return { ...acc, ...sectionValues };
    },
    {},
  );

  // Step 2: Create values object for processing
  const allValues = {
    ...state.candidateFields.candidateValues,
    ...state.candidateFields.hiddenValues,
    ...flattenedFormValues,
  };

  // Step 3: Get checkTypeIdRequirements (backend sends structured format)
  const currentCheckTypeIdRequirements = state.candidateFields.checkTypeIdRequirements || {};

  // Step 4: Call calculateIdRequirements with integrated hidden field processing
  const {
    updatedSections,
    updatedHiddenValues,
    idRequirementsMet,
    selectedIdDocuments,
    checkTypeIdRequirements,
  } = calculateIdRequirements(
    state.candidateFields.sections,
    state.application.id_uploads,
    allValues,
    state.candidateFields.fieldPathIndex,
    state.candidateFields.idRequirementsMet,
    state.candidateFields.selectedIdDocuments || {},
    state.candidateFields.hiddenFields || {},
    state.candidateFields.hiddenValues || {},
    state.candidateFields.hiddenDependencyCodes || {},
    currentCheckTypeIdRequirements,
    state.candidateFields.dynamicDependencyCodes || {},
    state.candidateFields.templateSources || {},
  );

  state.candidateFields.sections = updatedSections;
  state.candidateFields.hiddenValues = updatedHiddenValues;
  state.candidateFields.idRequirementsMet = idRequirementsMet;
  state.candidateFields.selectedIdDocuments = selectedIdDocuments;
  state.candidateFields.checkTypeIdRequirements = checkTypeIdRequirements;
};

const upsertUploadById = (state, upload) => {
  if (!upload || !upload.id) return;

  const meta = upload.metadata ?? {};

  const existedInId = state.application.id_uploads.some(u => u.id === upload.id);
  const isId = !!meta.id_type || upload.id_upload_detail !== undefined || existedInId;

  const targetKey = isId ? 'id_uploads' : 'other_uploads';
  const otherKey = isId ? 'other_uploads' : 'id_uploads';

  // Remove any stale copy from the opposite bucket to avoid duplicates
  state.application[otherKey] = state.application[otherKey].filter(u => u.id !== upload.id);

  const arr = state.application[targetKey];
  const idx = arr.findIndex(u => u.id === upload.id);

  if (idx === -1) {
    arr.push({
      ...upload,
      metadata: { ...(upload.metadata ?? {}) },
      // Only ID uploads carry id_upload_detail
      id_upload_detail: isId ? (upload.id_upload_detail ?? null) : undefined,
    });
    return;
  }

  const existing = arr[idx];
  arr[idx] = {
    ...existing,
    ...upload,
    metadata: {
      ...(existing.metadata ?? {}),
      ...(upload.metadata ?? {}),
    },
    ...(isId
      ? {
          id_upload_detail:
            upload.id_upload_detail !== undefined
              ? upload.id_upload_detail
              : (existing.id_upload_detail ?? null),
        }
      : {}),
  };
};

export const getCandidateApplication = createAsyncThunk('getCandidateApplication', async token => {
  const res = await apiRequest(`candidate_applications/${token}`, {}, 'get');
  const data = res?.result ?? res ?? {};
  return data;
});

export const cancelCandidateChecks = createAsyncThunk(
  'cancelCandidateChecks',
  async ({ token, data }) => {
    const res = await apiRequest(`candidate_applications/${token}/cancel_checks`, data, 'post');
    return res.result;
  },
);

export const saveCandidateChecksValues = markAsGuarded(
  createAsyncThunk('saveCandidateChecksValues', async ({ token, data }) => {
    const res = await apiRequest(`candidates_checks_values/token/${token}`, data, 'post');
    return res.result;
  }),
);

export const getCandidateUpload = createAsyncThunk('getCandidateUpload', async ({ token, id }) => {
  const res = await apiRequest(`candidates/token/${token}/uploads/${id}`, {}, 'get');
  return res.result;
});

export const signCandidateUpload = createAsyncThunk(
  'signCandidateUpload',
  async ({ token, params }) => {
    const res = await apiRequest(`candidates/token/${token}/uploads/sign_upload`, params, 'get');
    return res.result;
  },
);

export const createCandidateUpload = markAsGuarded(
  createAsyncThunk('createCandidateUpload', async ({ token, params }) => {
    const res = await apiRequest(`candidates/token/${token}/uploads`, params, 'post');
    return res.result;
  }),
);

export const reprocessStaticContent = createAction('reprocessStaticContent');

export const updateCandidateUpload = markAsGuarded(
  createAsyncThunk('updateCandidateUpload', async ({ token, params }) => {
    const res = await apiRequest(`candidates/token/${token}/uploads/${params.id}`, params, 'put');
    return res.result;
  }),
);

export const deleteCandidateUpload = markAsGuarded(
  createAsyncThunk(
    'deleteCandidateUpload',
    async ({ token, id, fieldCodes = null }, { dispatch, getState }) => {
      // Check if upload being deleted is a selfie before deletion
      const state = getState();
      const candidateAppState = state.candidateApplication;

      // Check if it's a selfie in current state
      const uploadToDelete = [
        ...(candidateAppState.application.other_uploads || []),
        ...(candidateAppState.application.id_uploads || []),
      ].find(u => u.id === id);

      const isSelfie = uploadToDelete?.metadata?.selfie === true;
      let hasAssociatedIdUploadDetails = false;

      // Check if any id_uploads reference this selfie. Note: id_uploads[].id_upload_detail
      // .selfie_upload_id is only ever synced into Redux by getCandidateApplication — creating
      // a new selfie doesn't update it locally, even though the backend (see
      // IdVerificationService::handleSelfieUpload) already re-points every id_upload_detail to
      // the new selfie server-side. So on a replace (new selfie created, then old one deleted —
      // the standard retake flow), this would stale-read as "still referenced" and trigger an
      // unnecessary full refetch below. A newer selfie already present in other_uploads (which
      // *does* update reliably) means the backend has already handled the re-pointing, so this
      // is a replacement, not an orphaning — skip the refetch in that case.
      if (isSelfie) {
        const hasNewerSelfie = (candidateAppState.application.other_uploads || []).some(
          upload => upload.metadata?.selfie === true && upload.id !== id,
        );
        hasAssociatedIdUploadDetails =
          !hasNewerSelfie &&
          (candidateAppState.application.id_uploads || []).some(
            upload => upload.id_upload_detail?.selfie_upload_id === id,
          );
      }

      // Perform deletion
      const res = await apiRequest(`candidates/token/${token}/uploads/${id}`, {}, 'delete');

      // If fieldCodes were provided, we need to save the updated candidateValues to the backend
      // to ensure the removed fields are persisted immediately (not just on next form save)
      if (fieldCodes && Array.isArray(fieldCodes) && fieldCodes.length > 0) {
        const currentCandidateValues =
          state.candidateApplication.candidateFields.candidateValues || {};

        // Remove the fieldCodes from candidateValues
        const updatedCandidateValues = { ...currentCandidateValues };
        fieldCodes.forEach(code => {
          if (code) {
            delete updatedCandidateValues[code];
          }
        });

        // Save the updated values to the backend immediately
        await dispatch(
          saveCandidateChecksValues({
            token,
            data: {
              values: updatedCandidateValues,
            },
          }),
        );
      }

      // If selfie was deleted and had associated id_upload_details, refetch application data
      if (isSelfie && hasAssociatedIdUploadDetails) {
        await dispatch(getCandidateApplication(token));
      }

      return { result: res.result, fieldCodes };
    },
  ),
);

export const verifyIdDocument = markAsGuarded(
  createAsyncThunk('verifyIdDocument', async ({ token, data }) => {
    const res = await apiRequest(`candidates/token/${token}/id_verification/verify`, data, 'post');
    return res.result;
  }),
);

export const updateIdUploadDetail = markAsGuarded(
  createAsyncThunk('updateIdUploadDetail', async ({ token, uploadId, data }) => {
    const res = await apiRequest(
      `candidates/token/${token}/id_upload_details`,
      { upload_id: uploadId, ...data },
      'put',
    );
    return res.result;
  }),
);

export const submitCandidateApplication = markAsGuarded(
  createAsyncThunk(
    'submitCandidateApplication',
    async ({ token, user_agent = null, re_consent = false }) => {
      const res = await apiRequest(
        `candidate_checks/token/${token}`,
        { user_agent, ...(re_consent ? { re_consent: true } : {}) },
        'post',
      );
      return res.result;
    },
  ),
);

export const updateCandidateCheck = markAsGuarded(
  createAsyncThunk('updateCandidateCheck', async ({ token, id, data }) => {
    const res = await apiRequest(`candidate_checks/token/${token}/id/${id}`, data, 'put');
    return res.result;
  }),
);

export const saveCustomReference = markAsGuarded(
  createAsyncThunk('saveCustomReference', async ({ token, id, referees, user_agent }) => {
    const res = await apiRequest(
      `candidate_checks/token/${token}/id/${id}/custom_reference`,
      { referees, user_agent },
      'post',
    );
    return res.result;
  }),
);

export const createReplacementReferee = markAsGuarded(
  createAsyncThunk('createReplacementReferee', async ({ token, id, refereeId }) => {
    const res = await apiRequest(
      `candidate_checks/token/${token}/id/${id}/custom_reference/replacement`,
      { referee_id: refereeId },
      'post',
    );
    return res.result;
  }),
);

export const getCandidateReferenceTypeformParams = createAsyncThunk(
  'getCandidateReferenceTypeformParams',
  async ({ token, id, replacement = false }) => {
    const res = await apiRequest(
      `candidate_checks/token/${token}/id/${id}/candidate_reference_typeform_params`,
      { replacement },
      'get',
    );
    return res.result;
  },
);

export const saveCheckFormAnswers = markAsGuarded(
  createAsyncThunk('saveCheckFormAnswers', async ({ token, id, params }) => {
    const res = await apiRequest(
      `candidate_checks/token/${token}/id/${id}/save_form_answers`,
      params,
      'post',
    );
    return res.result;
  }),
);

export const acicConsent = createAsyncThunk(
  'acicConsent',
  async ({ token, id }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(`services/acic/${id}/token/${token}/consent`, {}, 'post');
      return res.result;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const createDrugScreenOrder = createAsyncThunk(
  'createDrugScreenOrder',
  async ({ token, id }) => {
    const res = await apiRequest(`services/i3/${id}/token/${token}/drug_screen/order`, {}, 'post');
    return res.result;
  },
);

export const getI3DrugScreenSites = createAsyncThunk(
  'getI3DrugScreenSites',
  async ({ token, id, postal_code, search_radius, sub_check_id }) => {
    const params = { postal_code };
    if (search_radius) params.search_radius = search_radius;
    if (sub_check_id) params.sub_check_id = sub_check_id;
    const res = await apiRequest(
      `services/i3/${id}/token/${token}/drug_screen/sites`,
      params,
      'post',
    );
    return res.result;
  },
);

export const scheduleDrugScreen = createAsyncThunk(
  'scheduleDrugScreen',
  async ({
    token,
    id,
    sub_check_id,
    collection_site_link_id,
    site_name,
    site_address,
    site_city,
    site_state,
    site_phone,
  }) => {
    const res = await apiRequest(
      `services/i3/${id}/token/${token}/drug_screen/schedule`,
      {
        sub_check_id,
        collection_site_link_id,
        site_name,
        site_address,
        site_city,
        site_state,
        site_phone,
      },
      'post',
    );
    return res.result;
  },
);

export const handleDocumentRequestUploadComplete = createAsyncThunk(
  'handleDocumentRequestUploadComplete',
  async ({ token, upload_id }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        `document_requests/token/${token}/upload`,
        { upload_id },
        'post',
      );
      return res.result;
    } catch (err) {
      // Preserve API response shape so component can read error?.error?.log (e.g. document_request_inactive)
      return rejectWithValue(err);
    }
  },
);

export const checkDocumentRequestStatus = createAsyncThunk(
  'checkDocumentRequestStatus',
  async ({ token, document_request_id }) => {
    const res = await apiRequest(
      `document_requests/token/${token}/status?document_request_id=${document_request_id}`,
      {},
      'get',
    );
    return res.result;
  },
);

export const updateCandidateSections = createAction('candidateApplication/updateCandidateSections');
export const updateHiddenValues = createAction('candidateApplication/updateHiddenValues');
export const updateFormValues = createAction('candidateApplication/updateFormValues');
export const updateCandidateValues = createAction('candidateApplication/updateCandidateValues');
export const recalculateIdRequirements = createAction(
  'candidateApplication/recalculateIdRequirements',
);
export const getFormByRefereeId = createAsyncThunk(
  'getFormByRefereeId',
  async ({ token, refereeId }) => {
    const res = await apiRequest(`forms/token/${token}/referee/${refereeId}`, {}, 'get');
    return res.result;
  },
);

const candidateApplicationSlice = createSlice({
  name: 'candidateApplication',
  initialState,
  extraReducers: builder => {
    builder
      .addCase(getCandidateApplication.fulfilled, (state, action) => {
        const application =
          action.payload && typeof action.payload === 'object' ? action.payload : {};

        const safeCheckValues =
          application.check_values && typeof application.check_values === 'object'
            ? application.check_values
            : { values: {}, system_values: {} };

        state.application = {
          ...state.application,
          ...application,
          id_uploads: Array.isArray(application.id_uploads) ? application.id_uploads : [],
          other_uploads: Array.isArray(application.other_uploads) ? application.other_uploads : [],
          check_values: safeCheckValues,
        };

        state.application.id_uploads = (state.application.id_uploads || []).map(u => ({
          ...u,
          metadata: { ...(u.metadata ?? {}) },
          id_upload_detail: u.id_upload_detail ?? null,
        }));

        state.application.other_uploads = (state.application.other_uploads || []).map(u => ({
          ...u,
          metadata: { ...(u.metadata ?? {}) },
        }));

        const sections = Array.isArray(application?.candidate_fields?.candidate_sections)
          ? application.candidate_fields.candidate_sections
          : [];

        const templateSources = application?.candidate_fields?.data_sources ?? {};

        state.candidateFields = {
          sections: processStaticContent(sections, templateSources),
          templateSources,
          hiddenFields: camelCaseInnerKeys(application?.candidate_fields?.hidden_fields ?? {}),
          candidateValues: application.check_values?.values || {},
          hiddenValues: application?.candidate_fields?.hidden_values || {},
          formValues: {}, // Initialize empty form values
          sectionFieldPrefixes: application?.candidate_fields?.section_field_prefixes || {},
          fieldPathIndex: camelCaseInnerKeys(application?.candidate_fields?.field_path_index || {}),
          wrapperValidators: camelCaseInnerKeys(
            application?.candidate_fields?.wrapper_validators || {},
          ),
          excludeOnSubmitCodes: application?.candidate_fields?.exclude_on_submit_codes || [],
          saveSelectedIdListCodes: application?.candidate_fields?.save_selected_id_list_codes || [],
          dynamicDependencyCodes: application?.candidate_fields?.dynamic_dependency_codes || {},
          hiddenDependencyCodes: application?.candidate_fields?.hidden_dependency_codes || {},
          validationDependencyCodes:
            application?.candidate_fields?.validation_dependency_codes || {},
          idRequirementsDependencyCodes:
            application?.candidate_fields?.id_requirements_dependency_codes || [],
          hasIdRequirements: application?.candidate_fields?.has_id_requirements || false,
          idRequirementsMet: application?.candidate_fields?.id_requirements_met || {},
          checkTypeIdRequirements: application?.candidate_fields?.check_type_id_requirements || {},
          selectedIdDocuments: application?.candidate_fields?.selected_id_documents || {},
          photoIdOptions: application?.candidate_fields?.photo_id_options || [],
          dobIdOptions: application?.candidate_fields?.dob_id_options || [],
        };

        state.signatureRequired = (application.check_types || []).find(
          checkType => checkType.signature_required,
        );

        updateIdRequirements(state);

        state.fetched = true;
      })
      .addCase(saveCandidateChecksValues.fulfilled, (state, { payload }) => {
        state.application.check_values = payload;

        if (payload?.values) {
          // Merge new values with existing candidateValues instead of replacing
          state.candidateFields.candidateValues = {
            ...state.candidateFields.candidateValues,
            ...payload.values,
          };
        }
      })
      .addCase(getCandidateUpload.fulfilled, (state, { payload }) => {
        upsertUploadById(state, payload);
        updateIdRequirements(state);
      })
      .addCase(createCandidateUpload.fulfilled, (state, { payload, meta }) => {
        const sentMeta = meta?.arg?.params?.metadata || {};
        const isSelfie = sentMeta?.selfie === true || payload?.metadata?.selfie === true;

        upsertUploadById(state, {
          ...payload,
          metadata: { ...(payload.metadata ?? {}), ...sentMeta },
        });
        updateIdRequirements(state);
      })
      .addCase(updateCandidateUpload.fulfilled, (state, { payload }) => {
        // Without this, a metadata correction (e.g. syncUploadAttempts re-stamping an existing
        // upload's attempts count) persists to the backend but Redux's id_uploads cache keeps
        // the pre-correction value — a later remount (navigating away and back, no full
        // reload) would then rehydrate confirmedAttempts from that stale count.
        upsertUploadById(state, payload);
        updateIdRequirements(state);
      })
      .addCase(deleteCandidateUpload.fulfilled, (state, { payload }) => {
        // Payload structure: { result: <deletedId>, fieldCodes: <array|null> }
        // result is the deleted upload ID from the backend
        const deletedId =
          typeof payload?.result === 'number'
            ? payload.result
            : typeof payload?.result === 'string'
              ? parseInt(payload.result, 10)
              : null;
        if (deletedId == null) {
          return;
        }

        // Get fieldCodes from payload (passed from DocumentUpload)
        // These are just the codes (strings) extracted from displayFields, ready to use
        const fieldCodesFromPayload = payload?.fieldCodes;

        // Remove fields from candidateValues if fieldCodes are provided
        if (
          fieldCodesFromPayload &&
          Array.isArray(fieldCodesFromPayload) &&
          fieldCodesFromPayload.length > 0
        ) {
          const updatedCandidateValues = { ...state.candidateFields.candidateValues };
          fieldCodesFromPayload.forEach(code => {
            if (code) {
              delete updatedCandidateValues[code];
            }
          });
          state.candidateFields.candidateValues = updatedCandidateValues;
          // Note: Values are persisted to backend immediately in the deleteCandidateUpload thunk
          // This ensures the cleaned candidateValues are synced with the backend right away
        }

        // A deleted back upload leaves its front sibling's id_upload_detail.back_upload_id
        // pointing at an id that no longer exists — isDualUploadComplete (id-requirements.js)
        // only checks that this field is non-null, so without clearing it here the front would
        // still read as a complete dual pair (and the list "Complete") until the next full
        // refetch happens to pull the corrected, backend-nulled value. The front's prior
        // confirmed_by_cand must also be cleared — it was confirmed against a pairing that no
        // longer exists, so the candidate needs to confirm again once the pair is re-verified
        // (no new verify call runs until the front is replaced too, so nothing else would clear
        // this in the meantime).
        state.application.id_uploads = (state?.application?.id_uploads || []).map(u =>
          u?.id_upload_detail?.back_upload_id === deletedId
            ? {
                ...u,
                id_upload_detail: {
                  ...u.id_upload_detail,
                  back_upload_id: null,
                  confirmed_by_cand: false,
                },
              }
            : u,
        );

        // Always remove from state to update requirements and points calculation
        state.application.id_uploads = state?.application?.id_uploads?.filter(
          u => u.id !== deletedId,
        );
        state.application.other_uploads = state?.application?.other_uploads?.filter(
          u => u.id !== deletedId,
        );

        updateIdRequirements(state);
      })
      .addCase(verifyIdDocument.fulfilled, (state, { payload, meta }) => {
        const uploadId = meta?.arg?.data?.upload_id;
        if (!uploadId) return;

        upsertUploadById(state, {
          id: uploadId,
          id_upload_detail: payload ?? null,
        });
        updateIdRequirements(state);
      })
      .addCase(updateCandidateCheck.fulfilled, (state, { payload }) => {
        state.application.candidate_checks = state.application.candidate_checks.map(
          candidate_check => (candidate_check.id === payload.id ? payload : candidate_check),
        );
      })
      .addCase(updateIdUploadDetail.fulfilled, (state, { payload, meta }) => {
        const uploadId = meta?.arg?.uploadId;
        if (!uploadId) return;

        // Update the id_upload_detail in the corresponding id_upload
        const idUpload = state.application.id_uploads.find(upload => upload.id === uploadId);
        if (idUpload) {
          idUpload.id_upload_detail = { ...idUpload.id_upload_detail, ...payload };
        }

        // confirmed_by_cand (candidate confirming extracted fields) can flip whether this
        // upload is now eligible to satisfy a list's requirement (see isUploadReadyToCount) —
        // recompute so a list waiting on this confirmation updates immediately.
        updateIdRequirements(state);
      })
      .addCase(saveCustomReference.fulfilled, (state, { payload }) => {
        state.application.candidate_checks = state.application.candidate_checks.map(
          candidate_check => (candidate_check.id === payload.id ? payload : candidate_check),
        );
      })
      .addCase(saveCheckFormAnswers.fulfilled, (state, { payload }) => {
        state.application.candidate_checks = state.application.candidate_checks.map(
          candidate_check => (candidate_check.id === payload.id ? payload : candidate_check),
        );
      })
      .addCase(acicConsent.fulfilled, (state, { payload }) => {
        state.application.candidate_checks = state.application.candidate_checks.map(
          candidate_check => (candidate_check.id === payload.id ? payload : candidate_check),
        );
      })
      .addCase(createDrugScreenOrder.fulfilled, (state, { payload }) => {
        state.application.candidate_checks = state.application.candidate_checks.map(
          candidate_check => (candidate_check.id === payload.id ? payload : candidate_check),
        );
      })
      .addCase(scheduleDrugScreen.fulfilled, (state, { payload }) => {
        if (payload.all_scheduled && state.application.check_values) {
          state.application.check_values.system_values = {
            ...(state.application.check_values.system_values ?? {}),
            us_medical_screen_status: 'completed',
          };
        }
      })
      .addCase(getFormByRefereeId.fulfilled, (state, { payload }) => {
        state.application.candidate_checks = [
          ...state.application.candidate_checks,
          payload.reference_check,
        ];
        state.application.forms = [...state.application.forms, payload.candidate_form];
      })
      .addCase(updateCandidateSections, (state, action) => {
        const preservedByCode = collectRequirementsMetByCode(state.candidateFields.sections);
        state.candidateFields = {
          ...state.candidateFields,
          sections: preserveRequirementsMet([...action.payload], preservedByCode),
        };
      })
      .addCase(updateCandidateValues, (state, action) => {
        const { updatedCandidateValues } = action.payload;
        state.candidateFields.candidateValues = updatedCandidateValues;
      })
      .addCase(updateHiddenValues, (state, action) => {
        state.candidateFields = {
          ...state.candidateFields,
          hiddenValues: { ...state.candidateFields.hiddenValues, ...action.payload },
        };
      })
      .addCase(updateFormValues, (state, action) => {
        const { sectionKey, formValues } = action.payload;

        // Completely replace the section's formValues (don't merge with existing)
        state.candidateFields.formValues = {
          ...state.candidateFields.formValues,
          [sectionKey]: formValues, // Complete replacement, not merge
        };
        // ID requirements will be handled by dependency hooks if needed
      })
      .addCase(recalculateIdRequirements, (state, action) => {
        // If action.payload contains latestFormValues, use them instead of Redux state
        if (action.payload?.latestFormValues) {
          const allValues = {
            ...state.candidateFields.candidateValues,
            ...state.candidateFields.hiddenValues,
            ...action.payload.latestFormValues,
          };

          const {
            updatedSections,
            updatedHiddenValues,
            idRequirementsMet,
            selectedIdDocuments,
            checkTypeIdRequirements,
          } = calculateIdRequirements(
            state.candidateFields.sections,
            state.application.id_uploads,
            allValues,
            state.candidateFields.fieldPathIndex,
            state.candidateFields.idRequirementsMet,
            state.candidateFields.selectedIdDocuments || {},
            state.candidateFields.hiddenFields || {},
            state.candidateFields.hiddenValues || {},
            state.candidateFields.hiddenDependencyCodes || {},
            state.candidateFields.checkTypeIdRequirements || {},
            state.candidateFields.dynamicDependencyCodes || {},
            state.candidateFields.templateSources || {},
          );

          state.candidateFields.sections = updatedSections;
          state.candidateFields.hiddenValues = updatedHiddenValues;
          state.candidateFields.idRequirementsMet = idRequirementsMet;
          state.candidateFields.selectedIdDocuments = selectedIdDocuments;
          state.candidateFields.checkTypeIdRequirements = checkTypeIdRequirements;
        } else {
          updateIdRequirements(state);
        }
      })
      .addCase(reprocessStaticContent, state => {
        if (!state.application.candidate_fields?.candidate_sections) return;

        const sections = Array.isArray(state.application.candidate_fields.candidate_sections)
          ? state.application.candidate_fields.candidate_sections
          : [];

        const templateSources = state.candidateFields.templateSources ?? {};
        state.candidateFields.sections = processStaticContent(sections, templateSources);
      });
  },
});

export const selectCandidateApplication = state => state.candidateApplication;
export default candidateApplicationSlice.reducer;
